// Supabase client — data + storage only. Auth is handled by Clerk.
//
// Clerk → Supabase bridge: Clerk issues a JWT signed with the same secret as
// Supabase. We inject Clerk's JWT into every Supabase request via the new
// `accessToken` option (added to supabase-js in 2024). Supabase reads the
// `sub` claim from the Clerk JWT and uses it as auth.uid() for RLS.
//
// Setup: see Part 1 of cucina-setup-guide.md — Clerk integration in Supabase
// dashboard, plus a JWT template named "supabase" in Clerk dashboard.

import { createClient } from "@supabase/supabase-js";

// `import.meta.env` is Vite-only. In Vercel's Node runtime (api/inbound.js
// pulls this module in transitively via receipts.js) it's undefined, so
// guard the access to keep the module loadable in both contexts.
const env = import.meta.env ?? {};
const url  = env.VITE_SUPABASE_URL;
const anon = env.VITE_SUPABASE_ANON_KEY;

if (!url || !anon) {
  console.warn(
    "Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. " +
    "Copy .env.example to .env and fill these in."
  );
}

// The Clerk getToken function — set once at app start by setClerkTokenGetter().
// Default returns null so unauthenticated calls don't crash.
let clerkTokenGetter = async () => null;

export function setClerkTokenGetter(fn) {
  clerkTokenGetter = fn || (async () => null);
}

export const supabase = (url && anon)
  ? createClient(url, anon, {
      // Disable Supabase's own auth — Clerk owns auth state.
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
      },
      global: {
        // Attach the current Clerk JWT to every request.
        fetch: async (input, init = {}) => {
          const token = await clerkTokenGetter();
          const headers = new Headers(init.headers || {});
          if (token) {
            headers.set("Authorization", `Bearer ${token}`);
          }
          return fetch(input, { ...init, headers });
        },
      },
    })
  : null;

export const supabaseConfigured = !!(url && anon);

// Photo storage helpers. Convention: <user_id>/<entity>/<uuid>.<ext>
export const PHOTOS_BUCKET = "cucina-photos";

export async function uploadPhoto(file, entity, userId) {
  if (!supabase) throw new Error("Supabase not configured");
  if (!userId) throw new Error("Not signed in");
  const ext = (file.name?.split(".").pop() || "jpg").toLowerCase();
  const key = `${userId}/${entity}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage
    .from(PHOTOS_BUCKET)
    .upload(key, file, { contentType: file.type || "image/jpeg", upsert: false });
  if (error) throw error;
  return key;
}

export async function getPhotoUrl(path, expiresIn = 3600) {
  if (!supabase || !path) return null;
  const { data, error } = await supabase.storage
    .from(PHOTOS_BUCKET).createSignedUrl(path, expiresIn);
  if (error) {
    console.warn("Photo URL fetch failed", path, error.message);
    return null;
  }
  return data?.signedUrl || null;
}

export async function deletePhoto(path) {
  if (!supabase || !path) return;
  await supabase.storage.from(PHOTOS_BUCKET).remove([path]);
}

// Helpers for image conversion
export async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function fileMediaType(file) {
  return file?.type || "image/jpeg";
}
